import React from 'react';
import ReactDOM from 'react-dom/client';
import _ from 'lodash';
import moment from 'moment';
import GwTaskColumn from './GwTaskColumn';
import GwTaskItem from './GwTaskItem';
import { DndContext, DragOverlay } from '@dnd-kit/core';

/** @typedef { import('../../../app/services/TaskService.js').TaskColumnObj } TaskColumnObj */
/** @typedef { import('../../../app/models/TaskLinkModel.js').TaskLinkItem } TaskLinkItem */
/** @typedef { import('../../../app/models/TaskListModel.js').TaskListItem } TaskListItem */
/** @typedef { import('../../../app/models/TaskBoardModel.js').TaskBoardItem } TaskBoardItem */
/** @typedef { import('../../../app/models/TaskBoardTagModel.js').TaskBoardTagItem } TaskBoardTagItem */
/** @typedef { import('../../../app/models/UserModel.js').UserItem } UserItem */

/**
 * @typedef {object} TaskStateObj
 * @property {string} dataId - Identifiant de la donnée
 * @property {string} model - Modèle de la donnée
 * @property {string} csrf - Jeton CSRF
 * @property {Array<TaskLinkItem>} taskLinks - Liste des tâches liées
 * @property {Array<TaskListItem>} taskLists - Liste des listes de tâches
 * @property {?TaskLinkItem} movableTaskLink - Tâche liée en cours de déplacement
 * @property {?TaskBoardItem} taskBoard - Tableau de tâches
 * @property {boolean} addTaskListBtnIsDisabled - Désactivation du bouton pour ajouter une liste de tâches
 * @property {boolean} createTaskListBtnIsDisabled - Désactivation du bouton pour créer une nouvelle liste de tâches
 * @property {?number} currentTaskListId - Identifiant de la liste de tâches actuelle
 * @property {string} newTaskLinkDeadlineDate - Date limite de la nouvelle tâche liée
 * @property {React.RefObject<any>} reduceBtnNode - Référence du bouton pour réduire la liste des tâches
 * @property {React.RefObject<any>} datePickerInputRef - Référence de l'input pour le datepicker
 * @property {boolean} reduced - État de réduction de la liste des tâches
 * @property {boolean} reduceOption - Si la liste peut-être réduite
 * @property {string} newTaskListName - Nom de la nouvelle liste de tâches
 * @property {Array<TaskColumnObj>} taskColumns - Colonnes des tâches
 * @property {boolean} canBeReinitialized - Si les tâches peuvent être réinitialisées dans la colonne To Do
 * @property {string} autoDeadlineDateAttribute - Si la date limite des tâches peut être appliquée en fonction de la date de début de l'item
 * @property {string} avatarApiUrl - URL de l'API pour les avatars des utilisateurs
 */

/**
 * @typedef {object} GwTaskProps
 * @property {string} dataId - Identifiant de la donnée
 * @property {string} model - Modèle de la donnée
 * @property {string} csrf - Jeton CSRF
 * @property {string} reduced - État de réduction de la liste des tâches
 * @property {string} reduceOption - Si la liste peut-être réduite
 * @property {string} canBeReinitialized - Si les tâches peuvent être réinitialisées dans la colonne To Do
 * @property {string} autoDeadlineDateAttribute - Si la date limite des tâches peut être appliquée en fonction de la date de début de l'item
 */

class Task extends React.Component {
  /**
   * @param {GwTaskProps} props
   */
  constructor(props) {
    super(props);
    this.state = /**@type {TaskStateObj}*/ ({
      dataId: props.dataId,
      model: props.model,
      csrf: props.csrf,
      reduceOption: props.reduceOption === 'true',
      reduced: props.reduced === 'true',
      taskLinks: [],
      taskLists: [],
      taskBoard: null,
      addTaskListBtnIsDisabled: true,
      createTaskListBtnIsDisabled: true,
      currentTaskListId: null,
      avatarApiUrl: '',
      canBeReinitialized: props.canBeReinitialized === 'true',
      autoDeadlineDateAttribute: props.autoDeadlineDateAttribute,
      newTaskLinkDeadlineDate: '',
      reduceBtnNode: React.createRef(),
      datePickerInputRef: React.createRef(),
      newTaskListName: '',
      movableTaskLink: null,
      taskColumns: [{
        id: 0,
        name: 'To Do',
        taskLinks: /** @type {Array<TaskLinkItem>}*/([])
      },{
        id: 1,
        name: 'In Progress',
        taskLinks: /** @type {Array<TaskLinkItem>}*/([])
      },{
        id: 2,
        name: 'Done',
        taskLinks: /** @type {Array<TaskLinkItem>}*/([])
      }]
    });
  }

  componentDidMount() {
    var taskLinkApiurl = `/api/task-link/_search/${this.state.model}/${this.state.dataId}`;

    fetch(taskLinkApiurl, {
      headers: {
        Authorization: 'gestiaweb'
      }
    }).then(res => res.json()).then(result => {
      this.setState({
        taskLinks: result.taskLinks,
        taskLists: result.taskLists,
        avatarApiUrl: result.avatarApiUrl,
        taskBoard: result.taskBoard
      });
    });
  }

  /**
   * Lors du début du déplacement d'une tâche liée
   * @param {import('@dnd-kit/core').DragStartEvent} event - Événement de début de déplacement d'une tâche liée
   * @returns {void}
   */
  onDragStart(event) {
    var { active } = event,
        movableTaskLink = this.state.taskLinks.find(t => t.id === active.id);

    this.setState({ movableTaskLink });
  }

  /**
   * Lors de la fin du déplacement d'une tâche liée
   * @param {import('@dnd-kit/core').DragEndEvent} event - Événement de fin de déplacement d'une tâche liée
   * @returns {void}
   */
  onDragEnd(event) {
    var { active, over } = event,
        activeCurrent = /** @type {any} */(active.data.current),
        overCurrent = /** @type {any} */(/** @type {any} */(over).data.current),
        findedTaskLinks;

    if (activeCurrent.taskColumnId === overCurrent.taskColumnId) {
      return;
    }

    fetch('/api/task-link/_update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'gestiaweb'
      },
      body: JSON.stringify({
        id: active.id,
        state: overCurrent.taskColumnId,
        _csrf: this.state.csrf
      })
    }).then(res => res.json()).then(result => {

      findedTaskLinks = /** @type {TaskLinkItem}*/(this.state.taskLinks.find(task => {
        return task.id === active.id;
      }));
      
      findedTaskLinks.state = /** @type {0|1|2} */(/** @type {any} */(over).id);

      this.setState({
        taskLinks: this.state.taskLinks
      });
    });


    this.setState({ movableTaskLink: null });
  }

  /**
   * Lors du clic sur le bouton pour réinitialiser les tâches liées afin de les passer de Done à To Do
   */
  onClickOnResetBtn() {
    if(confirm('Voulez-vous vraiment réinitialiser les tâches ?')) {
      fetch('/api/task-link/_reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'gestiaweb'
        },
        body: JSON.stringify({
          model: this.state.model,
          dataId: this.state.dataId,
          _csrf: this.state.csrf
        })
      }).then(res => res.json()).then(taskLinks => {
        this.setState({
          taskLinks
        });
      });
    }
  }

  /**
   * Lors du changement de date dans le datepicker permettant de créer une nouvelle tâche
   * @param {string} dateValue - Date sélectionnée
   * @param {object} inputRef - Référence de l'input
   */
  onDateChange(dateValue, inputRef) {
    this.setState({
      newTaskLinkDeadlineDate: moment(dateValue).format('YYYY-MM-DD 08:00:00')
    }, () => {
      this.setState({
        datePickerInputRef: inputRef
      });
    });
  }

  /**
   * Lors du clic sur le bouton permettant d'appliquer une date limite à toutes les tâches de la colonne To Do par rapport à l'item
   */
  onClickOnSetDeadlineDateToItem() {
    var apiUrl = `/api/task-link/_updatedeadlines/${this.state.model}/${this.state.dataId}`;

    if(confirm('Voulez-vous vraiment appliquer la date limite de ces tâches ?')) {
      fetch(apiUrl, {
        headers: {
          Authorization: 'gestiaweb'
        }
      }).then(res => res.json()).then(newToDoTaskLinks => {
        var updatedTaskLinks = this.state.taskLinks.map(taskLink => {
          if (taskLink.state === 0) {
            var newTaskLink = /** @type {Array<TaskLinkItem>} */(newToDoTaskLinks).find(newTaskLink => newTaskLink.id === taskLink.id);
            return newTaskLink ? newTaskLink : taskLink;
          }
          return taskLink;
        });

        this.setState({
          taskLinks: updatedTaskLinks
        });
      });
    }
  }

  /**
   * Lors du clic sur le bouton pour supprimer les tâches terminées
   */
  onClickOnDeleteDoneTasks() {
    var apiUrl = `/api/task-link/_deleteall/${this.state.model}/${this.state.dataId}`;

    if(confirm('Voulez-vous vraiment supprimer toutes les tâches terminées de ce tableau ?')) {
      fetch(apiUrl, {
        headers: {
          Authorization: 'gestiaweb'
        }
      }).then(res => res.json()).then(taskLinks => {
        this.setState({ taskLinks });
      });
    }
  }

  /**
   * Lors du clic sur le bouton pour supprimer une tâche liée
   * @param {number} id - Identifiant de la tâche liée à supprimer
   */
  onClickOnDeleteBtn(id) {
    var apiDeleteUrl = `/api/task-link/_delete/${id}`;

    if(confirm('Voulez-vous vraiment supprimer cette tâche ?')) {
      fetch(apiDeleteUrl, {
        headers: {
          Authorization: 'gestiaweb'
        }
      }).then(res => res.json()).then(taskLinks => {
        this.setState({
          taskLinks: this.state.taskLinks.filter(taskLink => taskLink.id !== id)
        });
      });
    }
  }

  /**
   * Lors du changement de valeur dans la liste des tâches que l'on peut utiliser
   * @param {import('react').ChangeEvent} event - Événement de changement de valeur dans la liste des tâches que l'on peut utilser
   */
  onChangeOnSelect(event) {
    var target = /** @type {HTMLSelectElement}*/(event.target),
        taskListId = parseInt(target.value, 10);

    if (taskListId) {
      this.setState({
        addTaskListBtnIsDisabled: false,
        currentTaskListId: taskListId
      });
    } else {
      this.setState({
        addTaskListBtnIsDisabled: true,
        currentTaskListId: null
      });
    }
  }

  /**
   * Lors du clic sur le bouton pour ajouter une liste de tâches
   * @param {import('react').MouseEvent} event - Événement de clic sur le bouton pour ajouter une liste de tâches
   */
  onClickOnUseTaskList(event) {
    var taskListId = this.state.currentTaskListId;

    fetch('/api/task-list/_use', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'gestiaweb'
      },
      body: JSON.stringify({
        taskListId,
        model: this.state.model,
        data_id: parseInt(this.state.dataId, 10),
        _csrf: this.state.csrf
      })
    }).then(res => res.json()).then(taskLinks => {
      console.log(taskLinks);
      this.setState({
        taskLinks
      });
    });
  }

  /**
   * Lors du clic sur le bouton pour réduire ou agrandir la liste des tâches
   */
  onClickOnReduceBtn() {
    this.setState({
      reduced: !this.state.reduced
    }, () => {
      this.state.reduceBtnNode.current.querySelector('.material-icons').textContent = (this.state.reduced) ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
    });
  }

  /**
   * Lors du changement de valeur sur le champ de texte pour le nom de la nouvelle liste
   * @param {import('react').ChangeEvent} event - Événement de changement de valeur de l'input
   */
  onChangeOnNewTaskList(event) {
    var target = /** @type {HTMLInputElement}*/(event.target),
        newTaskListName = target.value;

    this.setState({
      newTaskListName
    }, () => {
      this.setState({
        createTaskListBtnIsDisabled: newTaskListName.length < 5,
      });
    });
  }

  /**
   * Lors du clic sur le bouton pour enregistrer une nouvelle liste à partir des tâces à faire
   */
  onClickOnCreateList() {
    var formattedTaskLinks = /** @type {Array<TaskLinkItem>}*/(this.state.taskLinks.filter(taskLink => taskLink.state === 0));

    formattedTaskLinks = /** @type {Array<any>} */(formattedTaskLinks.map(taskLink => {
      return {
        userId: taskLink.userId ?? taskLink.task?.userId,
        description: taskLink.description ?? taskLink.task.description,
        notes: taskLink.notes ?? taskLink.task?.notes,
        external_link: taskLink.external_link ?? taskLink.task?.external_link
      };
    }));

    if(confirm('Voulez-vous vraiment enregistrer cette liste avec les tâches se trouvant dans la colonne To Do ?')) {
      fetch('/api/task-list/_add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'gestiaweb'
        },
        body: JSON.stringify({
          name: this.state.newTaskListName,
          taskLinks: formattedTaskLinks,
          _csrf: this.state.csrf
        })
      }).then(res => res.json()).then(result => {
        this.setState({
          taskLists: [...this.state.taskLists, result]
        });
      });
    }
  }

  render() {
    var taskColumns = this.state.taskColumns.map((taskColumn) => {
      switch(taskColumn.id) {
        case 0:
          taskColumn.taskLinks = this.state.taskLinks.filter((item) => item.state === 0);
          break;
        case 1:
          taskColumn.taskLinks = this.state.taskLinks.filter((item) => item.state === 1);
          break;
        case 2:
          taskColumn.taskLinks = this.state.taskLinks.filter((item) => item.state === 2);
          break;
      }
      return taskColumn;
    });

    var className = (this.state.reduced) ? 'task task--reduced' : 'task';

    var toDoTaskLinks = this.state.taskLinks.filter(taskLink => taskLink.state === 0);

    var boardBackgroundStyle = {};

    if(this.state.taskBoard?.background_image) {
      boardBackgroundStyle.backgroundImage = `url(${this.state.taskBoard.background_image})`;
    }

    return (
      <div className={className}>
        <div className="task__header">
          <div className="task__title">
            {this.state.taskBoard?.name ? (
              <h4 className="task__title-text">Tableau : {this.state.taskBoard.name}</h4>
            ) : (
              <h4 className="task__title-text">Liste des tâches</h4>
            )}
            {this.state.reduceOption ? (
              <button className="task__title-btn" onClick={this.onClickOnReduceBtn.bind(this)} ref={this.state.reduceBtnNode}>
                <span className="material-icons">keyboard_arrow_down</span>
              </button>
            ) : (
              ''
            )}
          </div>
          <div className="task__list-action">
            {toDoTaskLinks.length && !this.state.taskBoard ? (
              <div className="task__list-action-group">
                <p className="paragraph--bold paragraph--white-space-nowrap">Enregistrer une liste</p>
                <input
                  aria-label="Nom de votre liste"
                  type="text"
                  className="task__input"
                  placeholder="Nom de votre liste"
                  value={this.state.newTaskListName}
                  onChange={this.onChangeOnNewTaskList.bind(this)}
                />
                <button
                  disabled={this.state.createTaskListBtnIsDisabled}
                  className="btn btn--small btn--only-svg"
                  onClick={this.onClickOnCreateList.bind(this)}
                >
                  <span className="material-icons">save</span>
                </button>
              </div>
            ) : (
              ''
            )}
            <div className="task__list-action-divider"></div>
            {!this.state.taskBoard ? (
              <div className="task__list-action-group">
                <p className="paragraph--bold paragraph--white-space-nowrap">Ajouter une liste</p>
                <select className="task__input" onChange={this.onChangeOnSelect.bind(this)}>
                  <option value="">Sélectionnez une valeur</option>
                  {this.state.taskLists.map((taskList, index) => (
                    <option key={index} value={taskList.id}>{taskList.name}</option>
                  ))}
                </select>
                <button
                  disabled={this.state.addTaskListBtnIsDisabled}
                  className="btn btn--small btn--only-svg"
                  onClick={this.onClickOnUseTaskList.bind(this)}
                >
                  <span className="material-icons">add</span>
                </button>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
        <DndContext onDragStart={this.onDragStart.bind(this)} onDragEnd={this.onDragEnd.bind(this)}>
          <div className="task__lists">
            {taskColumns.map((taskColumn, index) => (
              <GwTaskColumn
                key={index}
                taskLinks={this.state.taskLinks}
                taskColumn={taskColumn}
                onClickOnDeleteBtn={this.onClickOnDeleteBtn.bind(this)}
                onClickOnResetBtn={this.onClickOnResetBtn.bind(this)}
                onDateChange={this.onDateChange.bind(this)}
                onClickOnSetDeadlineDateToItem={this.onClickOnSetDeadlineDateToItem.bind(this)}
                onClickOnDeleteDoneTasks={this.onClickOnDeleteDoneTasks.bind(this)}
                canBeReinitialized={this.state.canBeReinitialized}
                autoDeadlineDateAttribute={this.state.autoDeadlineDateAttribute}
                taskBoard={this.state.taskBoard}
                dataId={this.state.dataId}
                model={this.state.model}
                movableTaskLink={this.state.movableTaskLink}
                avatarApiUrl={this.state.avatarApiUrl}
              />
            ))}
            <DragOverlay>
              {this.state.movableTaskLink ? (
                <GwTaskItem
                  taskLink={this.state.movableTaskLink}
                  taskColumn={{ id: this.state.movableTaskLink.state }}
                  onClickOnDeleteBtn={this.onClickOnDeleteBtn.bind(this)}
                  avatarApiUrl={this.state.avatarApiUrl}
                  isDraggingOverlay
                />
              ) : null}
            </DragOverlay>
          </div>
        </DndContext>
        {this.state.taskBoard?.background_image ? (
          <div className="task__background" style={boardBackgroundStyle}></div>
        ) : ''}
      </div>
    );
  }
}

var taskNode = document.getElementById('task');
if (taskNode) {
  var root = ReactDOM.createRoot(taskNode);
  root.render(
    <Task
      model={taskNode.getAttribute('data-model')}
      dataId={taskNode.getAttribute('data-id')}
      csrf={taskNode.getAttribute('data-csrf')}
      reduced={taskNode.getAttribute('data-reduced')}
      reduceOption={taskNode.getAttribute('data-reduce-option')}
      canBeReinitialized={taskNode.getAttribute('data-can-be-reinitialized')}
      autoDeadlineDateAttribute={taskNode.getAttribute('data-auto-deadline-date-attribute')}
    />
  );
}
